import { useState } from 'react'
import { ContactDrawer } from 'app/components/contacts/ContactDrawer'
import Spinner from 'app/components/Spinner'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'app/components/ui/tabs'
import type { ContactJson } from 'app/types/entities'
import { DemoPersonSheet } from './DemoPersonSheet'
import { DEMO_FAMILIES } from './demoFamilies'
import { FamilyLeadsTable } from './FamilyLeadsTable'
import type { Family, FamilyPerson } from 'app/types/related-contacts'
import { recipientFor } from 'lib/relatedContacts'
import { useWorkspaceFamilies } from './useWorkspaceFamilies'
import { WorkflowSimulation } from './WorkflowSimulation'

function DemoTab() {
  const [selection, setSelection] = useState<{ person: FamilyPerson; family: Family }>()
  const open = (person: FamilyPerson, family: Family) => setSelection({ person, family })

  return (
    <div className="flex flex-col gap-4">
      <ul className="grid gap-2 text-body-sm text-default sm:grid-cols-2">
        {DEMO_FAMILIES.map((family) => (
          <li key={family.id} className="rounded-lg border border-default bg-surface p-3">
            {family.story}
          </li>
        ))}
      </ul>
      <FamilyLeadsTable families={DEMO_FAMILIES} onOpen={open} />
      <WorkflowSimulation />
      <DemoPersonSheet selection={selection} onClose={() => setSelection(undefined)} onOpen={open} />
    </div>
  )
}

function WorkspaceTab() {
  const { loading, error, families, leadCount } = useWorkspaceFamilies()
  const [drawerContact, setDrawerContact] = useState<ContactJson>()

  if (loading) return <Spinner />
  if (error) return <p className="text-body-sm text-red-600">Could not build families: {error}</p>

  const people = families.flatMap((f) => (f.primary?.leadStatus ? [f.primary, ...f.members] : f.members))
  const multi = families.filter((f) => f.members.length > 0)
  const reachedThrough = people.filter((p) =>
    families.some((f) => f.members.includes(p) && recipientFor(p, f).kind === `primary`),
  )
  const noAdult = families.filter((f) => !f.primary)

  return (
    <div className="flex flex-col gap-4">
      <p className="text-body-sm text-default" data-testid="related-contacts-summary">
        Of the first {leadCount} active leads: <strong>{multi.length}</strong> families have more than one person,{' '}
        <strong>{reachedThrough.length}</strong> leads are reached through someone else,{' '}
        <strong>{people.filter((p) => (p.age ?? 18) < 18).length}</strong> look under 18, and{' '}
        <strong>{noAdult.length}</strong> have no adult to contact. Built in the browser from PMS guarantors and shared
        cell numbers, read-only.
      </p>
      <FamilyLeadsTable families={families} onOpen={(person) => setDrawerContact(person.contact)} />
      <ContactDrawer
        openDrawer={!!drawerContact}
        contact={drawerContact}
        onClose={() => setDrawerContact(undefined)}
        onChange={setDrawerContact}
      />
    </div>
  )
}

export function RelatedContactsPrototype() {
  return (
    <div className="flex flex-col gap-4 p-4 md:p-6" data-testid="related-contacts-prototype">
      <div>
        <h1 className="text-heading-md font-semibold text-strong">Related contacts</h1>
        <p className="text-body-sm text-subtle">
          Prototype of families and the primary contact. Nothing on this page is saved or sent.
        </p>
      </div>
      <Tabs defaultValue="demo">
        <TabsList>
          <TabsTrigger value="demo">Demo families</TabsTrigger>
          <TabsTrigger value="workspace">This workspace</TabsTrigger>
        </TabsList>
        <TabsContent value="demo" className="mt-4">
          <DemoTab />
        </TabsContent>
        <TabsContent value="workspace" className="mt-4">
          <WorkspaceTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
